// ===========================================
// MAIN APP COMPONENT
// ===========================================
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next'; // Import i18n
import { AppProviders } from '@/app/providers';
import { router } from '@/app/router';
import { ToastProvider } from '@/components/admin/ui';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { isAdminDomain } from '@/utils/domain';
import { queryClient } from '@/app/queryClient';
import { getAvailableLanguages } from '@/services/languageService'; // Public service
import { SystemContentProvider } from '@/contexts/SystemContentContext';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import './App.css';

/**
 * Main App Component
 * Sets up providers and router
 * Supports both public site and admin dashboard based on domain
 */
function App() {
  const isAdmin = isAdminDomain();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!isAdmin) {
      const initLanguage = async () => {
        try {
          // Get only languages with complete translations
          const availableLanguages = await getAvailableLanguages();
          const currentLangCode = i18n.language;

          // Check if current language is available (has complete translations)
          const isAvailable = availableLanguages.some(l => l.code === currentLangCode);

          if (!isAvailable) {
            // 1. Try device/browser language
            const deviceLang = navigator.language.split('-')[0];
            const matchingDevice = availableLanguages.find(l => l.code === deviceLang);

            if (matchingDevice) {
              // Device language has complete translations
              i18n.changeLanguage(matchingDevice.code);
            } else {
              // 2. Fallback to English (always available)
              i18n.changeLanguage('en');
            }
          }
        } catch (error) {
          console.error("Language initialization failed", error);
          // Fallback to English on error
          i18n.changeLanguage('en');
        }
      };

      initLanguage();
    }
  }, [isAdmin, i18n]);


  return (
    <QueryClientProvider client={queryClient}>
      <AppProviders>
        <ErrorBoundary>
          <LanguageProvider>
            {isAdmin ? (
              // Admin dashboard with toast notifications
              <ToastProvider>
                <RouterProvider router={router} />
              </ToastProvider>
            ) : (
              // Public site
              <SystemContentProvider>
                <ToastProvider>
                  <RouterProvider router={router} />
                </ToastProvider>
              </SystemContentProvider>
            )}
          </LanguageProvider>
        </ErrorBoundary>
      </AppProviders>
    </QueryClientProvider>
  );
}

export default App;
